import { normalizeTicketPromotion } from "../../../helpers/ticketPromotions";
import { resolveTicketsPerUnit } from "../../../helpers/ticketTypes";

// Pure builders for the guided "Crear evento" flow. They reproduce what EventForm.saveEvent sends,
// so the backend receives the same payload whichever screen created the event.

export const DEFAULT_CERT = {
  certTitulo: "Certificado de asistencia",
  certCuerpo: "Certificamos que {NOMBRE} asistió a {EVENTO} el {FECHA}.",
  certAsunto: "Tu certificado de {EVENTO}",
};

export const nuevaEntrada = () => ({ nombre: "", precio: "", cantidad: "", incluye: "1", desc: "", activo: true });

export const datosInicialesEvento = (negocio = "") => ({
  nombre: "",
  fecha: "",
  desde: "22:00",
  hasta: "05:00",
  descripcion: "",
  categoriaId: "",
  categoriaNombre: "",
  stateid: "",
  cityid: "",
  address: "",
  lat: "",
  lng: "",
  ciudadLabel: "",
  provinciaLabel: "",
  tipo: "gratis",
  capacidad: "",
  entradas: [nuevaEntrada()],
  imagenes: {},
  instagram: "",
  booking: true,
  maxPorPersona: "4",
  cupones: [],
  sponsors: [],
  pixel: "",
  pixelOn: false,
  cert: false,
  certTitulo: DEFAULT_CERT.certTitulo,
  certOrg: negocio,
  certFirmante: "",
  certCargo: "",
  certCuerpo: DEFAULT_CERT.certCuerpo,
  certMail: true,
  certAsunto: DEFAULT_CERT.certAsunto,
});

export const num = (v) => Number(String(v ?? "").replace(",", "."));

// EventForm treats only sports events as certificate-capable (certificatesEnabled = isSports && toggle).
export const esCategoriaDeporte = (nombre) => /deporte|sports/i.test(String(nombre || ""));

// Overnight events (22:00 -> 05:00) end the next calendar day; date-only math avoids timezone shifts.
export const fechaFin = (fecha, desde, hasta) => {
  if (!fecha || !desde || !hasta || hasta >= desde) return fecha;
  const [y, m, d] = fecha.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
};

export const sumaCupos = (entradas) => entradas.reduce((acc, e) => acc + (num(e.cantidad) || 0), 0);

export const faltaEntradas = (d) => {
  if (!(num(d.capacidad) > 0)) return "Poné el cupo total del evento";
  if (d.tipo === "gratis") return "";
  if (d.entradas.some((e) => !e.nombre.trim())) return "Hay una entrada sin nombre";
  const sinPrecio = d.entradas.find((e) => !(num(e.precio) > 0));
  if (sinPrecio) return `Falta el precio de ${sinPrecio.nombre}`;
  const sinCupo = d.entradas.find((e) => !(num(e.cantidad) > 0));
  if (sinCupo) return `Falta el cupo de ${sinCupo.nombre}`;
  const suma = sumaCupos(d.entradas);
  return suma > num(d.capacidad) ? `Las entradas suman ${suma} y el cupo es ${d.capacidad}` : "";
};

const CERT_KEYS_VACIAS = {
  certificateSubtitle: "",
  certificateSigner2Name: "",
  certificateSigner2Role: "",
  certificateEmailBody: "",
  certificateWinnersBody: "",
};

// Same 14 certificate keys EventForm always appends (all off/empty on create).
export const camposCertificado = (d = {}) => {
  const activo = Boolean(d.cert);
  return {
    certificatesEnabled: activo,
    certificateTitle: activo ? d.certTitulo || "" : "",
    certificateSubtitle: CERT_KEYS_VACIAS.certificateSubtitle,
    certificateBody: activo ? d.certCuerpo || "" : "",
    certificateSignerName: activo ? d.certFirmante || "" : "",
    certificateSignerRole: activo ? d.certCargo || "" : "",
    certificateSigner2Name: CERT_KEYS_VACIAS.certificateSigner2Name,
    certificateSigner2Role: CERT_KEYS_VACIAS.certificateSigner2Role,
    certificateIssuer: activo ? d.certOrg || "" : "",
    certificateEmailSubject: activo ? d.certAsunto || "" : "",
    certificateEmailBody: CERT_KEYS_VACIAS.certificateEmailBody,
    certificateSendEmail: Boolean(activo && d.certMail),
    certificateWinnersEnabled: false,
    certificateWinnersBody: CERT_KEYS_VACIAS.certificateWinnersBody,
  };
};

// ticketTypes JSON as EventForm sends it (status true on every new type; promotion off by default).
export const tiposDeEntrada = (entradas) =>
  entradas.map((e, idx) => ({
    name: e.nombre.trim(),
    description: "",
    price: num(e.precio),
    ticketsPerUnit: 1,
    capacity: num(e.cantidad),
    promotion: normalizeTicketPromotion({ enabled: false, ticketsRequired: 4, ticketsCharged: 2, label: "4x2" }),
    status: true,
    sortOrder: idx,
  }));

const coordValida = (v) => v !== "" && v != null && Number.isFinite(Number(v));

// Scalar fields of the create request, in EventForm's order.
export const camposCrearEvento = (d, { soloGratis = false } = {}) => {
  const isFree = soloGratis || d.tipo === "gratis";
  return {
    name: d.nombre.trim(),
    description: d.descripcion.trim(),
    categoryid: d.categoriaId,
    cityid: d.cityid,
    stateid: d.stateid,
    address: d.address.trim(),
    // Blank lat/lng are omitted: the backend geocodes the address only when the keys are absent.
    ...(coordValida(d.lat) && coordValida(d.lng) ? { lat: d.lat, lng: d.lng } : {}),
    startDate: d.fecha,
    endDate: fechaFin(d.fecha, d.desde, d.hasta),
    timeFrom: d.desde,
    timeTo: d.hasta,
    isFree,
    price: 0,
    booking: true,
    maxbooking: num(d.capacidad),
    maxTicketsPerUser: 4,
    instagram: "",
    ...camposCertificado(),
  };
};

export const formDataCrearEvento = (d, opts = {}) => {
  const fd = new FormData();
  Object.entries(camposCrearEvento(d, opts)).forEach(([k, v]) => fd.append(k, v));
  if (d.tipo === "pagas" && !opts.soloGratis) fd.append("ticketTypes", JSON.stringify(tiposDeEntrada(d.entradas)));
  return fd;
};

/* ---- Optional steps: partial updates (the backend only touches the fields present) ---- */

const nuevoUpdate = (id) => {
  const fd = new FormData();
  fd.append("id", id);
  return fd;
};

export const hayImagenes = (d) => Boolean(Object.values(d.imagenes || {}).some(Boolean) || d.instagram.trim());

export const formDataImagenes = (id, d) => {
  const fd = nuevoUpdate(id);
  const { portada, galeria, portadaMovil, galeriaMovil } = d.imagenes || {};
  if (portada) fd.append("image", portada);
  if (portadaMovil) fd.append("mobileimage", portadaMovil);
  if (galeria) fd.append("galleryimage", galeria);
  if (galeriaMovil) fd.append("gallerymobileimage", galeriaMovil);
  if (d.instagram.trim()) fd.append("instagram", d.instagram.trim());
  return fd;
};

// The update replaces ticket types by id, so every existing type travels, edited or not.
export const tiposConCondiciones = (remotos, entradas) => {
  const usados = new Set();
  return remotos.map((r, idx) => {
    const i = entradas.findIndex((e, j) => !usados.has(j) && e.nombre.trim() === r.name);
    const local = entradas[i >= 0 ? i : idx];
    if (i >= 0) usados.add(i);
    return {
      id: r._id || r.id,
      name: r.name,
      description: local ? local.desc.trim() : r.description || "",
      price: Number(r.price || 0),
      ticketsPerUnit: local ? Math.max(1, num(local.incluye) || 1) : resolveTicketsPerUnit(r, 1),
      capacity: Number(r.capacity || 0),
      promotion: normalizeTicketPromotion(r.promotion),
      status: local ? Boolean(local.activo) : !!r.status,
      sortOrder: Number(r.sortOrder || 0),
    };
  });
};

export const formDataCompra = (id, d, remotos = []) => {
  const fd = nuevoUpdate(id);
  fd.append("booking", d.booking);
  fd.append("maxTicketsPerUser", Math.max(1, num(d.maxPorPersona) || 1));
  if (d.tipo === "pagas" && remotos.length) {
    fd.append("ticketTypes", JSON.stringify(tiposConCondiciones(remotos, d.entradas)));
  }
  return fd;
};

export const formDataCertificados = (id, d, plantilla = null) => {
  const fd = nuevoUpdate(id);
  Object.entries(camposCertificado(d)).forEach(([k, v]) => fd.append(k, v));
  if (d.cert && plantilla) fd.append("certificateTemplate", plantilla);
  return fd;
};

export const formDataPixel = (id, d) => {
  const fd = nuevoUpdate(id);
  fd.append("marketingMetaPixelEnabled", String(Boolean(d.pixelOn && d.pixel.trim())));
  fd.append("marketingMetaPixelPixelId", d.pixel.trim());
  return fd;
};

export const formDataSponsor = (eventid, s) => {
  const fd = new FormData();
  fd.append("name", s.nombre.trim());
  fd.append("eventid", eventid);
  fd.append("image", s.file);
  return fd;
};

export const payloadCupon = (eventid, c) => ({
  code: c.codigo.trim(),
  discount: num(c.descuento),
  discountType: c.tipo,
  eventid,
  status: true,
});
